/**
 * Hunt list component with table/card views
 */

import { Table } from '../common';
import HuntCard from './HuntCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import './HuntList.css';

/**
 * Generates column definitions with action handlers
 * @param {Function} onEdit - Edit handler (hunt entries only)
 * @param {Function} onDelete - Delete handler (hunt entries only)
 * @returns {Array} Column definitions
 */
function getColumns(onEdit, onDelete, onFoundIt) {
  return [
    {
      key: 'distilleries',
      label: 'Distillery',
      sortable: false,
      render: (value) => value?.distillery_name || '-'
    },
    {
      key: 'bottle_name',
      label: 'Name',
      sortable: false
    },
    { key: 'type', label: 'Type', sortable: false, render: (value) => value || '-' },
    {
      key: 'priority',
      label: 'Priority',
      sortable: false,
      render: (value) => {
        const tier = value >= 8 ? 'high' : value >= 4 ? 'medium' : 'low';
        return (
          <span className={`hunt-priority hunt-priority--${tier}`}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'notes',
      label: 'Notes',
      sortable: false,
      render: (value) => value
        ? <span className="hunt-list__notes-cell" title={value}>{value}</span>
        : '-'
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '160px',
      render: (value, row) => (
          <div className="table__actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="hunt-found-btn"
              onClick={() => onFoundIt(row)}
              aria-label="Found it — add to collection"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Found It!
            </button>
            <button
              className="table__icon-btn"
              onClick={() => onEdit(row)}
              title="Edit"
              aria-label="Edit hunt entry"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="table__icon-btn table__icon-btn--danger"
              onClick={() => onDelete(row)}
              title="Delete"
              aria-label="Delete hunt entry"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        )
    }
  ];
}

/**
 * Hunt list with responsive table/card layout
 * @param {Object} props - Component props
 * @param {Array} props.items - Hunt entries
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement} List element
 */
export default function HuntList({ items, onEdit, onDelete, onFoundIt, loading }) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div className="hunt-card-list">
        {loading ? (
          <div className="hunt-card-list__loading">Loading...</div>
        ) : items.length === 0 ? (
          <div className="hunt-card-list__empty">No hunts yet — add a bottle you&apos;re searching for!</div>
        ) : (
          items.map(item => (
            <HuntCard
              key={item.id}
              hunt={item}
              onEdit={() => onEdit(item)}
              onDelete={() => onDelete(item)}
              onFoundIt={() => onFoundIt(item)}
            />
          ))
        )}
      </div>
    );
  }

  const columns = getColumns(onEdit, onDelete, onFoundIt);

  return (
    <Table
      columns={columns}
      data={items}
      loading={loading}
      emptyMessage="No hunts yet — add a bottle you're searching for!"
    />
  );
}
